import { makeStyles } from "tss-react/mui";
import { COST_CALCULATION_TYPE } from "@miplatform/constants";
import {
  ICategoryPricingDetailsResponse,
  IMaterialsResponse,
} from "@miplatform/types";
import { getCurrencyString } from "@miplatform/utils";
import { Box, Chip, Typography } from "@mui/material";
import { PricingSummaryTable } from "./PricingSummaryTable";

type CategoryPricingWithMaterials = ICategoryPricingDetailsResponse & {
  materials: IMaterialsResponse[];
};

const getColumnHeaders = (isMultiplier: boolean) => [
  "Items",
  isMultiplier ? "List" : "Cost",
  "Pre-Tax Sale Price",
  "After Tax Sale Price",
  "Net Sale Price",
  "Margin (%)",
  "Margin ($)",
];

const computeMarginFromNetSalesPrice = (
  netSalesPrice: number,
  cost: number
) => {
  const marginDollar = netSalesPrice - cost;
  const marginPercent =
    netSalesPrice !== 0 ? (marginDollar / netSalesPrice) * 100 : 0;
  return {
    marginDollar: Number(marginDollar.toFixed(2)),
    marginPercent: Number(marginPercent.toFixed(2)),
  };
};

const formatValue = (
  value: number | null | undefined,
  formatter: (value: number) => string
) => (value == null ? "--" : formatter(value));

export function CategoryPricingSummaryCard({
  category,
}: Readonly<{ category: CategoryPricingWithMaterials }>) {
  const { classes, theme } = useStyles();

  const isMultiplier = category.costCalculation === COST_CALCULATION_TYPE.LIST;
  const modifierValue = isMultiplier ? category.multiplier : category.markup;
  const modifierChipLabel = isMultiplier
    ? `Multiplier: ${(modifierValue ?? 1).toFixed(2)}`
    : `Markup: ${(modifierValue ?? 0).toFixed(0)}%`;

  const taxRate = category.materials[0]?.taxRate ?? 0;

  const rows = category.materials.map((material) => {
    const listPrice = material.manufacturerListPrice ?? 0;
    const cost = material.cost ?? listPrice;
    const preTaxSalePrice =
      listPrice *
      (isMultiplier ? modifierValue ?? 1 : 1 + (modifierValue ?? 0) / 100);
    const afterTaxSalePrice =
      preTaxSalePrice * (1 + (material.taxRate ?? 0) / 100);

    const initialMargins =
      material.netSalesPrice != null
        ? computeMarginFromNetSalesPrice(material.netSalesPrice, cost)
        : null;
    const marginPercent =
      material.marginPercent ?? initialMargins?.marginPercent ?? null;
    const marginDollar =
      material.marginDollar ?? initialMargins?.marginDollar ?? null;

    return [
      `${material.manufacturerPartNumber} | ${material.category.itemDescription}`,
      getCurrencyString(listPrice),
      getCurrencyString(preTaxSalePrice),
      getCurrencyString(afterTaxSalePrice),
      formatValue(material.netSalesPrice, getCurrencyString),
      formatValue(marginPercent, (value) => `${value.toFixed(2)}%`),
      formatValue(marginDollar, getCurrencyString),
    ];
  });

  return (
    <span className={classes.printWrapper}>
      <Box className={classes.card}>
        <Box className={classes.header}>
          <Typography fontWeight={600}>{category.categoryName}</Typography>
          <Box className={classes.chips}>
            <Chip
              size="small"
              variant="outlined"
              sx={{
                border: `1px solid ${theme.palette.teal.main}`,
                backgroundColor: theme.palette.teal.light,
                color: theme.palette.teal.main,
                borderRadius: 1,
              }}
              label={modifierChipLabel}
            />
            <Chip
              size="small"
              variant="outlined"
              sx={{
                border: `1px solid ${theme.palette.info.main}`,
                backgroundColor: theme.palette.info.light,
                color: theme.palette.info.main,
                borderRadius: 1,
              }}
              label={`Tax: ${taxRate.toFixed(2)}%`}
            />
          </Box>
        </Box>
        <PricingSummaryTable
          columns={getColumnHeaders(isMultiplier)}
          rows={rows}
        />
      </Box>
    </span>
  );
}

const useStyles = makeStyles()((theme) => ({
  printWrapper: {
    marginBlockStart: theme.spacing(2),
  },
  card: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2),
    padding: theme.spacing(2),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    backgroundColor: theme.palette.grey[50],
    "@media print": {
      boxDecorationBreak: "clone",
    },
  },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(2),
  },
  chips: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
  },
}));
